import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import { useAuth } from "../../context/AuthProvider";
import API_BASE_URL from "../../config/apiConfig";
import Swal from "sweetalert2";
import back from "../../assets/images/icon-back.svg";
import hide from "../../assets/images/hide.svg";
import show from "../../assets/images/show.svg";

interface TherapistCategory {
  key: string;
  value: string;
  priority: string;
  color: string;
}

const AddTherapist: React.FC = () => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    password: "",
    therapist_category: "",
    order_in_category: "" as string | number,
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [categories, setCategories] = useState<TherapistCategory[]>([]);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });
  const navigate = useNavigate();

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return "Parola trebuie să aibă cel puțin 8 caractere.";
    if (!/[A-Z]/.test(password)) return "Parola trebuie să conțină cel puțin o literă mare.";
    if (!/[a-z]/.test(password)) return "Parola trebuie să conțină cel puțin o literă mică.";
    if (!/\d/.test(password)) return "Parola trebuie să conțină cel puțin un număr.";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Parola trebuie să conțină cel puțin un caracter special.";
    return null;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/types/therapist-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setCategories(data.data || []);
      } catch (error) {
        console.error("Eroare la încărcarea categoriilor:", error);
      }
    };
    if (token) fetchCategories();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      Toast.fire({ icon: "error", title: passwordError });
      return;
    }

    const payload: any = {
      username: formData.username,
      email: formData.email,
      firstname: formData.firstName,
      lastname: formData.lastName,
      phone: formData.phone,
      password: formData.password,
      therapist_category: formData.therapist_category,
    };

    if (formData.order_in_category !== "" && formData.order_in_category !== null) {
      payload.order_in_category = Number(formData.order_in_category);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/add-therapist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const { detail } = await response.json();
        throw new Error(detail || "A apărut o eroare.");
      }

      Toast.fire({ icon: "success", title: "Terapeut adăugat cu succes!" });
      setTimeout(() => navigate("/webmaster/therapists"), 1500);
    } catch (err: any) {
      Toast.fire({ icon: "error", title: err.message || "Eroare la adăugare" });
    }
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />
      <div className="page-content">
        <h1 className="page-title">Adaugă Terapeut</h1>
        <div className="table-top">
          <div className="page-return">
            <button className="button-return" onClick={() => navigate(-1)}>
              <img src={back} alt="Înapoi" className="back-icon" /> Înapoi
            </button>
          </div>
        </div>
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <label>Username:</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Username"
                required
              />
            </div>
            <div className="form-row">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Adresa de e-mail"
                required
              />
            </div>
            <div className="form-row">
              <label>Nume:</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Nume"
                required
              />
            </div>
            <div className="form-row">
              <label>Prenume:</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Prenume"
                required
              />
            </div>
            <div className="form-row">
              <label>Telefon:</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefon (opțional)"
              />
            </div>
            <div className="form-row">
              <label>Tip încadrare:</label>
              <select
                name="therapist_category"
                value={formData.therapist_category}
                onChange={handleChange}
                required
              >
                <option value="">Selectează tip încadrare</option>
                {categories.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.value}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Ordine în categorie (opțional):</label>
              <input
                type="number"
                name="order_in_category"
                min={0}
                value={formData.order_in_category}
                onChange={handleChange}
                placeholder="Ordine în categorie"
              />
            </div>
            <div className="form-row password-field">
              <label>Parolă:</label>
              <input
                type={isPasswordVisible ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Parola"
                required
              />
              <button type="button" onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
                <img
                  className={isPasswordVisible ? "hide-password" : "show-password"}
                  src={isPasswordVisible ? hide : show}
                  alt={isPasswordVisible ? "Ascunde" : "Arată"}
                />
              </button>
            </div>
            <div className="form-row form-actions">
              <button className="cancel-btn" onClick={() => navigate(-1)} type="button">
                Anulează
              </button>
              <button className="primary-btn" type="submit">
                Adaugă
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTherapist;
